import React, { SyntheticEvent, useRef, useState } from 'react';
import { useEffect } from 'react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';

interface Props {
    name: string;
    className?: string;
    placeholder?: string;
    multiple?: boolean
    options: {
        label: string;
        value: string | number;
    }[];
    value?: string | number | string[] | number[];
    setValue: (value: any) => void
    leftIcon?: React.ReactNode;
    widthClass?: string
    disabled?: boolean
    arrowColor?: string;
}

const defaultProps = {
    name: 'select',
    options: [],
}


type NativeAttrs = Omit<React.ButtonHTMLAttributes<any>, keyof Props>
export type SelectProps = Props & typeof defaultProps & NativeAttrs

const Select: React.FC<SelectProps> = ({
    name,
    className,
    placeholder,
    multiple,
    options,
    value,
    setValue,
    leftIcon,
    disabled,
    widthClass = 'w-full',
    arrowColor = 'text-gray-600',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const toggleDropDown = () => !disabled && setIsOpen(!isOpen);
    const [selfValue, setSelfValue] = useState((!value && multiple) ? [] : value)

    const [selectedOpts, setSelectedOps] = useState([])
    const [selected, setSelected] = useState('');

    useEffect(()=> {
        setSelfValue((!value && multiple) ? [] : value)
    },[value])

    useEffect(() => {
        if (multiple) {
            setSelectedOps(options.filter(opt => (selfValue as any[]).includes(opt.value)));
        } else {
            setSelectedOps(options.filter(opt => opt.value === selfValue) ?? null);
        }
    }, [options, selfValue])

    useEffect(() => {
        if (multiple) {
            setSelected(selectedOpts.map(opt => opt.label).join(', ') || null);
        } else {
            setSelected(selectedOpts.length > 0 ? selectedOpts[0].label : null);
        }
    }, [selectedOpts])

    // let selectedOpts;
    // let selected;
    // if (multiple) {
    //     selectedOpts = options.filter(opt => (selfValue as any[]).includes(opt.value));
    //     selected = selectedOpts.map(opt => opt.label).join(', ') || null;
    // } else {
    //     selectedOpts = options.filter(opt => opt.value === selfValue) ?? null;
    //     selected = selectedOpts.length > 0 ? selectedOpts[0].label : null;
    // }

    const handleOptionClick = newValue => (e: SyntheticEvent) => {
        if (disabled) return;
        e.preventDefault();

        // on ferme uniquement en mode multiple
        !multiple && setIsOpen(false);

        if (multiple) {
            const updatedValue = (selfValue as any[]).includes(newValue) ?
                (selfValue as any[]).filter(v => v !== newValue) :
                (selfValue as any[]).concat([newValue]);
            setSelfValue(updatedValue as any)
            setValue(updatedValue as any)
            return
        }
        setSelfValue(newValue as any)
        setValue(newValue as any)
    }
    const ref = useRef();
    useOnClickOutside(ref, () => setIsOpen(false));


    const Option = (opt) => {

        const isSelected = selectedOpts.map(so => so.value).includes(opt.value)

        return (
            <a className={`block py-2.5 px-5 pr-10 hover:bg-blue-500 ${isSelected && 'bg-blue-100'} hover:text-white cursor-pointer`}
                onClick={handleOptionClick(opt.value)}>
                {opt.label}
                {isSelected && (
                    <span className="absolute right-0 z-10 items-center justify-center w-8 h-full pr-3 text-base font-normal leading-snug text-center text-gray-600 bg-transparent rounded">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </span>
                )}
            </a>
        )
    }

    return (
        <div ref={ref} className={widthClass}>
            <button type="button" name={name} className={`${className} ${widthClass} flex truncate select  text-left focus:outline-none  ${selected ? 'text-black' : 'text-gray-600'}`} onClick={toggleDropDown}>
                {leftIcon && (
                    <span className="flex-shrink-0 mr-4">{leftIcon}</span>
                )}
                <span className="flex-grow truncate">{selected ?? placeholder}</span>
                <span className={`right-0 z-10 items-center justify-center flex-shrink-0 w-5 h-full text-base font-normal leading-snug text-center ${arrowColor} bg-transparent rounded`}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </span>
            </button>
            <div className="absolute z-20">
                <div className={`${isOpen ? 'block' : 'hidden'} relative text-black border bg-white outline-none rounded shadow`}>
                    {options.map(opt => (
                        <Option key={opt.value} {...opt} />
                    ))}
                </div>

            </div>
        </div>
    )
}

Select.defaultProps = defaultProps;

export default React.memo(Select, (prevProps, nextProps) => {
    return prevProps.value === nextProps.value
        && prevProps.className === nextProps.className
        && prevProps.placeholder === nextProps.placeholder
        && prevProps.disabled === nextProps.disabled
})